import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import {
    Card,
    CardBody,
    Input,
} from "reactstrap"
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast, { Toaster } from "react-hot-toast"
import useApi from "common/ApiMiddlewere"
import { useLocation } from "react-router-dom"
import breadcrumbs from "common/Breadcrumbs"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import EmployeeSalaryTable from "./EmployeeSalaryTable"
import { exportDataExcel } from "components/Common/ExportData"
import { dateFormat, dateTimeFormat } from "pages/Common"

function EmployeeSalary() {
    document.title = `Employee Salary | Raja Rani Couture`
    let { pathname } = useLocation()
    const { getApi, postApi } = useApi()
    // useStates
    const [isEdit, setIsEdit] = useState(false)
    const [tableData, setTableData] = useState([])
    const [updateSalaryData, setUpdateSalaryData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [screenLoad, setScreenLoad] = useState(false)
    const [searchText, setSearchText] = useState("")
    const [displayStart, setDisplayStart] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(20)
    const [userFilter, setUserFilter] = useState(0)
    const [users, setUsers] = useState([])
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7))
    const [totalCash, setTotalCash] = useState(0)
    const [totalBank, setTotalBank] = useState(0)

    const pagePerSize = (pageSize, pagePerIndex, serchValue) => {
        setDisplayStart(pagePerIndex * pageSize - pageSize + 1)
        setSearchText(serchValue == undefined ? "" : serchValue)
        setRowsPerPage(pageSize)
    }

    useEffect(() => {
        getApi('Attendance/user-lookup').then(({ data, status }) => {
            if (status === 200) {
                setUsers(data)
            } else {
                toast.error(data, {
                    style: {
                        fontSize: "15px",
                    },
                })
            }
        })
    }, [])

    // user list api
    const salaryListApi = () => {
        setIsLoading(true)
        setScreenLoad(true)
        postApi("Attendance/emp-salary-details", {
            AttendanceUserId: (JSON.parse(localStorage.getItem("authUser")).RoleName == "Admin" || JSON.parse(localStorage.getItem("authUser")).RoleName == "Master") ? parseInt(userFilter) : JSON.parse(localStorage?.getItem("authUser")).AttendanceUserId,
            MonthOfSalary: month + "-01",
        }).then(({ data, status }) => {
            if (status === 200) {
                setTableData(data)
                // Normalize data: convert numeric values to strings for input fields
                const normalizedData = data.map(item => ({
                    ...item,
                    MonthlyAdvance: item.MonthlyAdvance != null ? String(item.MonthlyAdvance) : '',
                    MonthlyReceiveAdvance: item.MonthlyReceiveAdvance != null ? String(item.MonthlyReceiveAdvance) : '',
                    Cash: item.Cash != null ? String(item.Cash) : '',
                    Bank: item.Bank != null ? String(item.Bank) : '',
                    InvalidMonthlyAdvance: false,
                    InvalidMonthlyRecieveAdvance: false,
                    InvalidCashBank: false,
                }))
                setTotalCash(
                    normalizedData.reduce((sum, row) => sum + (Number(row.Cash) || 0), 0)
                );

                setTotalBank(
                    normalizedData.reduce((sum, row) => sum + (Number(row.Bank) || 0), 0)
                );

                setUpdateSalaryData(normalizedData)
            } else {
                toast.error(data, {
                    style: {
                        fontSize: "15px",
                    },
                })
            }
            setIsLoading(false)
            setScreenLoad(false)
        })
    }

    // user list api useEffect
    useEffect(() => {
        salaryListApi()
    }, [searchText, displayStart, rowsPerPage, userFilter, month])

    const generateSalarySlip = (employee) => {
        const doc = new jsPDF();
        // ---------------- Header ----------------
        doc.setFontSize(20);
        doc.setTextColor("#2E4053");
        doc.text("Raja Rani Couture", 105, 22, null, null, "center");

        // Horizontal line
        doc.setLineWidth(0.5);
        doc.line(14, 28, 196, 28);

        // ---------------- Employee Info ----------------
        doc.setFontSize(12);
        doc.setTextColor("#000");
        doc.setFont("helvetica", "bold");
        doc.text("Employee Information", 14, 38);

        doc.setFont("helvetica", "normal");
        doc.text(`Name: ${employee.FullName}`, 14, 46);
        doc.text(
            `Month: ${dateTimeFormat(employee.MonthOfSalary, "MMMM yyyy")}`,
            14,
            52
        );
        doc.text(
            `Date: ${employee.CreatedOn === "0001-01-01T00:00:00"
                ? "N/A"
                : dateFormat(employee.CreatedOn?.split("T")[0], "dd-MM-yyyy")
            }`,
            14,
            58
        );

        // ---------------- Salary Table ----------------
        const tableData = [
            ["Salary", `Rs. ${Math.round(Number(employee.CalculatedSalary - parseFloat(employee.MonthlyAdvance - parseFloat(employee.MonthlyReceiveAdvance)))).toLocaleString("en-IN")}`],
            ["Working Hours", employee.TotalTime || "0:00"],
            // ["Remarks", employee.Remarks || "N/A"],
        ];

        autoTable(doc, {
            startY: 65,
            head: [["Particulars", "Details"]],
            body: tableData,
            styles: {
                fontSize: 12,
            },
            headStyles: {
                fillColor: "#2980B9",
                textColor: "#FFF",
                fontStyle: "bold",
                halign: "start",  // <-- center headers
                // valign: "middle",  // optional: vertical center
            },
            bodyStyles: {
                fontSize: 11,
                halign: "start",  // <-- center body cells by default
            },
            alternateRowStyles: {
                fillColor: "#ECF0F1",
            },
            columnStyles: {
                0: { halign: "start" }, // Particulars column center
                1: { halign: "start" }, // Details column center
            },
            margin: { left: 14, right: 14 },
        });
        const finalY = doc.lastAutoTable.finalY || 90;
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.setTextColor("#7F8C8D");
        // Save PDF
        doc.save(
            `SalarySlip_${employee.FullName}_${dateTimeFormat(
                employee.MonthOfSalary,
                "MMMM_yyyy"
            )}.pdf`
        );
    };

    const handleInputChange = (
        CalculatedSalary,
        TotalAdvance,
        AttendanceUserId,
        fieldName,
        fieldValue
    ) => {
        // Get current state and update the specific field
        setUpdateSalaryData(prev => {
            const updatedData = prev.map(item => {
                if (item.AttendanceUserId === AttendanceUserId) {
                    // Get current values from state (not from parameters)
                    const currentMonthlyCashAdvance = fieldName === 'MonthlyCashAdvance' ? fieldValue : (item.MonthlyCashAdvance || 0);
                    const currentMonthlyBankAdvance = fieldName === 'MonthlyBankAdvance' ? fieldValue : (item.MonthlyBankAdvance || 0);
                    const currentMonthlyReceiveAdvance = fieldName === 'MonthlyReceiveAdvance' ? fieldValue : (item.MonthlyReceiveAdvance || 0);
                    const currentCash = fieldName === 'Cash' ? fieldValue : (item.Cash || 0);
                    const currentBank = fieldName === 'Bank' ? fieldValue : (item.Bank || 0);

                    const CashAdvance = currentMonthlyCashAdvance === '' ? 0 :
                        (parseFloat(currentMonthlyCashAdvance) || 0);
                    const BankAdvance = currentMonthlyBankAdvance === '' ? 0 :
                        (parseFloat(currentMonthlyBankAdvance) || 0);
                    const rec = currentMonthlyReceiveAdvance === '' ? 0 :
                        (parseFloat(currentMonthlyReceiveAdvance) || 0);
                    const cash = currentCash === '' ? 0 : (parseFloat(currentCash) || 0);
                    const Bank = currentBank === '' ? 0 : (parseFloat(currentBank) || 0);

                    const remainingAfterAdvance = CalculatedSalary - (CashAdvance + BankAdvance);
                    const remainingAfterReceive = remainingAfterAdvance - rec;
                    const totalPayment = cash + Bank;

                    let invalidAdvance = false;
                    let invalidReceive = false;
                    let invalidPayment = false;

                    if ((CashAdvance + BankAdvance) > CalculatedSalary) {
                        invalidAdvance = true;
                    }

                    if (rec > remainingAfterAdvance || rec > TotalAdvance) {
                        invalidReceive = true;
                    }

                    if (totalPayment > remainingAfterReceive) {
                        invalidPayment = true;
                    }

                    return {
                        ...item,
                        MonthlyAdvance: (CashAdvance + BankAdvance),
                        MonthlyCashAdvance: CashAdvance,
                        MonthlyBankAdvance: BankAdvance,
                        MonthlyReceiveAdvance: currentMonthlyReceiveAdvance,
                        Cash: currentCash,
                        Bank: currentBank,
                        PayableSalary: CalculatedSalary - (CashAdvance + BankAdvance),
                        InvalidMonthlyAdvance: invalidAdvance,
                        InvalidMonthlyRecieveAdvance: invalidReceive,
                        InvalidCashBank: invalidPayment,
                    };
                }
                return item;
            });
            return updatedData;
        });
    };

    const handleSubmit = (AttendanceUserId, Id, data) => {
        setScreenLoad(true)
        postApi("Attendance/emp-salary-add", {
            Id: Id || 0,
            date: month + "-01",
            AttendanceUserId: parseInt(AttendanceUserId),
            MonthlyAdvance: parseFloat(data.MonthlyAdvance || 0) || 0,
            MonthlyCashAdvance: parseFloat(data.MonthlyCashAdvance || 0) || 0,
            MonthlyBankAdvance: parseFloat(data.MonthlyBankAdvance || 0) || 0,
            MonthlyReceiveAdvance: parseFloat(data.MonthlyReceiveAdvance || 0) || 0,
            Cash: parseFloat(data.Cash || 0) || 0,
            Bank: parseFloat(data.Bank || 0) || 0,
            remarks: ''
        }).then(({ data, status }) => {
            setScreenLoad(false)
            if (status === 200) {
                toast.success("Successfully Added", { style: { fontSize: "15px" } });
                salaryListApi()
            } else {
                toast.error(data, { style: { fontSize: "15px" } });
            }
        });
    };

    const handleUpdate = (AttendanceUserId, Id) => {
        setIsEdit(true)
        setUpdateSalaryData(prev => {
            const updatedData = prev.map(item => {
                if (item.AttendanceUserId === AttendanceUserId) {
                    return {
                        ...item,
                        isEdit: true
                    };
                }
                return item;
            });
            return updatedData;
        });
    }

    const handleKeyPress = (event) => {
        const charCode = event.which || event.keyCode;
        if ((charCode < 48 || charCode > 57) && charCode != 46) {
            event.preventDefault();
        }
    };

    // table headers & accessor
    const columns = useMemo(
        () => [
            {
                Header: "Name",
                accessor: "FullName",
            },
            {
                Header: "Moth of Salary",
                // Header: () => (
                //     <div
                //         style={{
                //             textAlign: "center",
                //         }}
                //     >
                //         Moth of Salary
                //     </div>
                // ),
                accessor: "MonthOfSalary",
                width: "8%",
                Cell: ({ row }) => {
                    return (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center"
                        }}>
                            {dateTimeFormat(row.original.MonthOfSalary, 'MMMM')}
                        </div>
                    )
                },
            },
            {
                Header: "Total hrs",
                Header: () => (
                    <div
                        style={{
                            textAlign: "center",
                        }}
                    >
                        Total hrs
                    </div>
                ),
                accessor: "TotalTime",
                width: "8%",
                Cell: ({ row }) => {
                    return (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center"
                        }}>
                            {(row?.original?.TotalTime) || " 0:00"}
                        </div>
                    )
                },
            },
            {
                Header: "Total Salary",
                // Header: () => (
                //     <div
                //         style={{
                //             textAlign: "center",
                //         }}
                //     >
                //         Total Salary
                //     </div>
                // ),
                accessor: "CalculatedSalary",
                width: "8%",
                Cell: ({ row }) => {
                    return (
                        <div style={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "end",
                            textAlign: "end"
                        }}>
                            <i className="mdi mdi-currency-inr"></i>
                            {new Intl.NumberFormat('en-IN', {
                                currency: 'INR',
                                minimumFractionDigits: 2,
                            }).format((row?.original?.CalculatedSalary || 0))}
                        </div>
                    )
                },
            },
            {
                Header: "Cash Upad",
                accessor: "MonthlyCashAdvance",
                Cell: ({ row }) => (
                    <>
                        <Input
                            type="text"
                            placeholder="0"
                            disabled={row.original.Id > 0 && !row.original.isEdit}
                            onKeyPress={handleKeyPress}
                            onFocus={(e) => { e.target.select() }}
                            value={row.original.MonthlyCashAdvance ?? ''}
                            onChange={(e) =>
                                handleInputChange(row?.original?.CalculatedSalary, row?.original?.TotalAdvance, row.original.AttendanceUserId, 'MonthlyCashAdvance', e.target.value)
                            }
                            invalid={row?.original?.InvalidMonthlyAdvance}
                            className="form-control-sm"
                            style={{ width: "70px", textAlign: "right" }}
                        />
                    </>
                ),
            },
            {
                Header: "Bank Upad",
                accessor: "MonthlyBankAdvance",
                Cell: ({ row }) => (
                    <>
                        <Input
                            type="text"
                            placeholder="0"
                            disabled={row.original.Id > 0 && !row.original.isEdit}
                            onKeyPress={handleKeyPress}
                            onFocus={(e) => { e.target.select() }}
                            value={row.original.MonthlyBankAdvance ?? ''}
                            onChange={(e) =>
                                handleInputChange(row?.original?.CalculatedSalary, row?.original?.TotalAdvance, row.original.AttendanceUserId, 'MonthlyBankAdvance', e.target.value)
                            }
                            invalid={row?.original?.InvalidMonthlyAdvance}
                            className="form-control-sm"
                            style={{ width: "70px", textAlign: "right" }}
                        />
                    </>
                ),
            },
            {
                Header: "Total Upad",
                accessor: "MonthlyAdvance",
                Cell: ({ row }) => (
                    <>
                        <i className="mdi mdi-currency-inr"></i>
                        {new Intl.NumberFormat('en-IN', {
                            currency: 'INR',
                            minimumFractionDigits: 2,
                        }).format((row?.original?.MonthlyAdvance || 0))}
                    </>
                ),
            },
            {
                Header: "Payable Salary",
                Header: () => (
                    <div
                        style={{
                            textAlign: "center",
                        }}
                    >
                        Payable Salary
                    </div>
                ),
                accessor: "PayableSalary",
                width: "8%",
                Cell: ({ row }) => {
                    const monthlyAdvance = typeof row?.original?.MonthlyAdvance === 'string' && row?.original?.MonthlyAdvance !== ''
                        ? parseFloat(row?.original?.MonthlyAdvance) || 0
                        : (row?.original?.MonthlyAdvance || 0);
                    return (
                        <div style={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "end",
                            textAlign: "end"
                        }}>
                            <i className="mdi mdi-currency-inr"></i>
                            {new Intl.NumberFormat('en-IN', {
                                currency: 'INR',
                                minimumFractionDigits: 2,
                            }).format(((row?.original?.CalculatedSalary || 0) - monthlyAdvance))}
                        </div>
                    )
                },
            },
            {
                Header: "Advance",
                Header: () => (
                    <div
                        style={{
                            textAlign: "center",
                        }}
                    >
                        Advance
                    </div>
                ),
                accessor: "TotalAdvance",
                width: "8%",
                Cell: ({ row }) => {
                    return (
                        <div style={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "end",
                            textAlign: "end"
                        }}>
                            <i className="mdi mdi-currency-inr"></i>
                            {new Intl.NumberFormat('en-IN', {
                                currency: 'INR',
                                minimumFractionDigits: 2,
                            }).format((row?.original?.TotalAdvance))}
                        </div>
                    )
                },
            },
            {
                Header: "Receive Advance",
                accessor: "MonthlyReceiveAdvance",
                Cell: ({ row }) => (
                    <Input
                        type="text"
                        placeholder="0"
                        disabled={row.original.Id > 0 && !row.original.isEdit}
                        onKeyPress={handleKeyPress}
                        value={row.original.MonthlyReceiveAdvance ?? ''}
                        onChange={(e) =>
                            handleInputChange(row?.original?.CalculatedSalary, row?.original?.TotalAdvance, row.original.AttendanceUserId, 'MonthlyReceiveAdvance', e.target.value)
                        }
                        invalid={row?.original?.InvalidMonthlyRecieveAdvance}
                        className="form-control-sm"
                        style={{ width: "115px", textAlign: "right" }}
                    />
                ),
            },
            {
                Header: "Salary",
                // Header: () => (
                //     <div
                //         style={{
                //             textAlign: "center",
                //         }}
                //     >
                //         Salary
                //     </div>
                // ),
                accessor: "Salary",
                width: "8%",
                Cell: ({ row }) => {
                    const monthlyAdvance = typeof row?.original?.MonthlyAdvance === 'string' && row?.original?.MonthlyAdvance !== ''
                        ? parseFloat(row?.original?.MonthlyAdvance) || 0
                        : (row?.original?.MonthlyAdvance || 0);
                    const monthlyReceiveAdvance = typeof row?.original?.MonthlyReceiveAdvance === 'string' && row?.original?.MonthlyReceiveAdvance !== ''
                        ? parseFloat(row?.original?.MonthlyReceiveAdvance) || 0
                        : (row?.original?.MonthlyReceiveAdvance || 0);
                    return (
                        <div style={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "end",
                            textAlign: "end"
                        }}>
                            <i className="mdi mdi-currency-inr"></i>
                            {new Intl.NumberFormat('en-IN', {
                                currency: 'INR',
                                minimumFractionDigits: 2,
                            }).format(((row?.original?.CalculatedSalary || 0) - monthlyAdvance - monthlyReceiveAdvance))}
                        </div>
                    )
                },
            },
            {
                Header: "Cash",
                accessor: "Cash",
                Cell: ({ row }) => (
                    <Input
                        type="text"
                        placeholder="0"
                        disabled={row.original.Id > 0 && !row.original.isEdit}
                        onKeyPress={handleKeyPress}
                        value={row.original.Cash}
                        onChange={(e) =>
                            handleInputChange(row?.original?.CalculatedSalary, row?.original?.TotalAdvance, row.original.AttendanceUserId, 'Cash', e.target.value)
                        }
                        invalid={row?.original?.InvalidCashBank}
                        className="form-control-sm"
                        style={{ width: "70px", textAlign: "right" }}
                    />
                ),
            },
            {
                Header: "Bank",
                accessor: "Bank",
                Cell: ({ row }) => (
                    <Input
                        type="text"
                        placeholder="0"
                        disabled={row.original.Id > 0 && !row.original.isEdit}
                        onKeyPress={handleKeyPress}
                        value={row.original.Bank ?? ''}
                        onChange={(e) =>
                            handleInputChange(row?.original?.CalculatedSalary, row?.original?.TotalAdvance, row.original.AttendanceUserId, 'Bank', e.target.value)}
                        invalid={row?.original?.InvalidCashBank}
                        className="form-control-sm"
                        style={{ width: "70px", textAlign: "right" }}
                    />
                ),
            },
            {
                Header: "Action",
                width: "5%",
                accessor: "action",
                disableFilters: true,
                Cell: ({ row }) => {
                    return (
                        <ul className="list-unstyled hstack gap-1 mb-0 d-flex justify-content-center gap-2">
                            {((row.original.Id > 0 && !row.original.isEdit) || row?.original?.InvalidCashBank || row?.original?.InvalidMonthlyRecieveAdvance || row?.original?.InvalidMonthlyAdvance) ?
                                <>
                                    <li className="mytooltip">
                                        <i
                                            className="fs-6 far fa-save btn-pdf"
                                            style={{ color: "#808080b3", cursor: "not-allowed" }}
                                        ></i>
                                        <span className="mytext">Save</span>
                                    </li>
                                </>
                                :
                                <>
                                    <li className="mytooltip">
                                        <i
                                            className="fs-6 far fa-save btn-pdf"
                                            onClick={() => handleSubmit(row.original.AttendanceUserId, row.original.Id, row.original)}
                                            style={{ color: "blue", cursor: "pointer" }}
                                        ></i>
                                        <span className="mytext">Save</span>
                                    </li>
                                </>
                            }
                            {(row.original.Id == 0) ?
                                <>
                                    <li className="mytooltip">
                                        <i
                                            className="fs-6 fas fa-edit btn-edit"
                                            style={{ color: "#808080b3", cursor: "not-allowed" }}
                                        ></i>
                                        <span className="mytext">Edit</span>
                                    </li>
                                </>
                                :
                                <>
                                    <li className="mytooltip">
                                        <i
                                            className="fs-6 fas fa-edit btn-edit"
                                            onClick={() => handleUpdate(row.original.AttendanceUserId, row.original.Id, row.original)}
                                        ></i>
                                        <span className="mytext">Edit</span>
                                    </li>
                                </>
                            }

                            <li className="mytooltip">
                                <i
                                    className="fs-6 fas fa-file-pdf btn-pdf"
                                    onClick={() => generateSalarySlip(row.original)}
                                    style={{ color: "#f44336", cursor: "pointer" }}
                                ></i>
                                <span className="mytext">Salary Slip</span>
                            </li>
                        </ul>
                    )
                },
            },
        ], [tableData, isEdit])

    function writeDataToExcel() {
        setIsLoading(true)
        let header = ["Name", "Moth of Salary", "Total hrs", "Total Salary", "Cash Upad", "Bank Upad", "Total Upad", "Payable Salary", "Advance", "Receive Advance", "Salary", "Cash", "Bank"];
        const mainTitle = "Emaployee Salary";
        const widthObj = [{
            Key: "A",
            Value: 10,
        },
        {
            Key: "B",
            Value: 19,
        },
        {
            Key: "C",
            Value: 19,
        },
        {
            Key: "D",
            Value: 19,
        },
        {
            Key: "E",
            Value: 19,
        },
        {
            Key: "F",
            Value: 19,
        },
        {
            Key: "G",
            Value: 19,
        },
        {
            Key: "H",
            Value: 19,
        },
        {
            Key: "I",
            Value: 19,
        },
        {
            Key: "J",
            Value: 19,
        },
        ]
        const extention = "Empolyee_Salary.xlsx"
        let excelSheetData = []

        postApi("Attendance/emp-salary-details", {
            AttendanceUserId: (JSON.parse(localStorage.getItem("authUser")).RoleName == "Admin" || JSON.parse(localStorage.getItem("authUser")).RoleName == "Master") ? parseInt(userFilter) : JSON.parse(localStorage?.getItem("authUser")).AttendanceUserId,
            MonthOfSalary: month + "-01",
        }).then(({ data, status }) => {
            if (status === 200) {
                for (let index = 0; index < data.length; index++) {
                    let dataObj = {
                        Name: data[index].FullName,
                        MonthOfSalary: dateTimeFormat(data[index].MonthOfSalary, 'MMMM'),
                        Totalhrs: data[index].TotalTime,
                        TotalSalary: parseFloat(Math.round(data[index].CalculatedSalary)),
                        CashUpad: parseFloat(Math.round(data[index].MonthlyCashAdvance)),
                        BankUpad: parseFloat(Math.round(data[index].MonthlyBankAdvance)),
                        TotalUpad: parseFloat(Math.round(data[index].MonthlyAdvance)),
                        PayableSalary: parseFloat(Math.round(data[index].CalculatedSalary - data[index].MonthlyAdvance)),
                        Advance: parseFloat(Math.round(data[index].TotalAdvance)),
                        ReceiveAdvance: parseFloat(Math.round(data[index].MonthlyReceiveAdvance)),
                        Salary: parseFloat(Math.round(data[index].CalculatedSalary - data[index].MonthlyAdvance - data[index].MonthlyReceiveAdvance)),
                        Cash: parseFloat(Math.round(data[index].Cash)),
                        Bank: parseFloat(Math.round(data[index].Bank)),
                    }
                    excelSheetData.push(dataObj)
                }
                var fields = Object.keys(excelSheetData[0]);
                var sheetData = excelSheetData.map(function (row) {
                    return fields.map(function (fieldName) {
                        return row[fieldName];
                    });
                });
                setIsLoading(false)
                sheetData.unshift(header);
                exportDataExcel(sheetData, mainTitle, widthObj, extention)
            } else {
                toast.error(data, {
                    style: {
                        fontSize: "15px",
                    },
                })
            }
            setIsLoading(false)
            setScreenLoad(false)
        })

        setIsLoading(false)
    }

    return (
        <div className="page-content">
            <div className="container-fluid">
                {screenLoad && (
                    <div className="loading-overlay is-active">
                        <span className="fas fa-spinner fa-3x fa-spin"></span>
                    </div>
                )}
                <Breadcrumbs
                    title={breadcrumbs[pathname].parent}
                    breadcrumbItem={breadcrumbs[pathname].breadcrumbItem}
                    link={breadcrumbs[pathname].parentLink}
                />
                <Card>
                    <CardBody>
                        <EmployeeSalaryTable
                            columns={columns}
                            data={updateSalaryData}
                            isGlobalFilter={true}
                            isAddUserList={true}
                            customPageSize={10}
                            className="custom-header-css"
                            isLoading={isLoading}
                            pagePerSize={pagePerSize}
                            users={users}
                            userFilter={userFilter}
                            setUserFilter={setUserFilter}
                            writeDataToExcel={writeDataToExcel}
                            month={month}
                            setMonth={setMonth}
                            totalCash={totalCash}
                            totalBank={totalBank}
                        />
                    </CardBody>
                </Card>
            </div>
        </div>
    )
}
EmployeeSalary.propTypes = {
    preGlobalFilteredRows: PropTypes.any,
    onFilterChange: PropTypes.func,
}

export default EmployeeSalary
